// SAD-499 (SOL-03): alternatives, the human decision on them, and gaps. A decision
// is recorded beside the Solution Concept; it never rewrites the accepted approach
// or an earlier revision. Rejected alternatives and every decision stay visible.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { projectDataClient } from '../../../lib/connect/clients';
import { Choice, options, TextField } from '../PastPerformance/Fields';
const SOURCE_KINDS = [
    'customer_theme',
    'request_document',
    'touchpoint',
    'sme_note',
    'prior_material',
    'pp_evidence',
    'teammate_evidence',
    'attachment'
];
const GAP_KINDS = [
    'requirement',
    'capability',
    'staffing'
];
const FOLLOW_UPS = [
    'none',
    'teaming',
    'pp_data_call',
    'task',
    'customer_question',
    'new_follow_up'
];
const label = (v)=>v.replace(/_/g, ' ');
const when = (d)=>d ? d.toDate().toISOString().slice(0, 10) : '';
const blankEvidence = {
    sourceKind: 'request_document',
    sourceLabel: '',
    sourceRef: '',
    excerpt: ''
};
function AlternativeRow({ alt, rationale, onDecide }) {
    return /*#__PURE__*/ _jsxs("li", {
        "data-testid": Testids.captureSolution.altRow,
        className: "space-y-1 rounded-md border p-2",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 text-sm",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: alt.name
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        "data-testid": Testids.captureSolution.altStatus,
                        children: /*#__PURE__*/ _jsx(SpecraBadge, {
                            children: alt.status === 'selected' ? 'Working approach' : label(alt.status)
                        })
                    })
                ]
            }),
            alt.narrative && /*#__PURE__*/ _jsx("div", {
                className: "whitespace-pre-wrap text-sm",
                children: alt.narrative
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "text-xs",
                children: [
                    "Advantages: ",
                    alt.advantages || 'None recorded'
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "text-xs",
                children: [
                    "Concerns: ",
                    alt.concerns || 'None recorded'
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "text-xs",
                children: [
                    "Assumptions: ",
                    alt.assumptions || 'None recorded'
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1 pl-3 text-xs",
                children: alt.decisions.map((d)=>/*#__PURE__*/ _jsxs("li", {
                        "data-testid": Testids.captureSolution.altDecision,
                        children: [
                            label(d.decision),
                            " on ",
                            when(d.decidedAt),
                            ": ",
                            d.rationale
                        ]
                    }, d.id))
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.captureSolution.altSelect,
                        disabled: !rationale.trim() || alt.status === 'selected',
                        onClick: ()=>onDecide(alt, 'selected'),
                        children: "Select as working approach"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.captureSolution.altReject,
                        disabled: !rationale.trim() || alt.status === 'rejected',
                        onClick: ()=>onDecide(alt, 'rejected'),
                        children: "Reject"
                    })
                ]
            })
        ]
    });
}
function GapRow({ gap, note, onResolve }) {
    return /*#__PURE__*/ _jsxs("li", {
        "data-testid": Testids.captureSolution.gapRow,
        className: "space-y-1 rounded-md border p-2",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 text-sm",
                children: [
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        children: label(gap.kind)
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        "data-testid": Testids.captureSolution.gapStatus,
                        children: /*#__PURE__*/ _jsx(SpecraBadge, {
                            children: gap.status
                        })
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: gap.description
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1 pl-3 text-xs",
                children: gap.evidence.map((e)=>/*#__PURE__*/ _jsxs("li", {
                        "data-testid": Testids.captureSolution.gapEvidenceItem,
                        children: [
                            "Evidence — ",
                            label(e.sourceKind),
                            ": ",
                            e.sourceLabel,
                            e.sourceRef && ` (${e.sourceRef})`,
                            e.excerpt && ` — ${e.excerpt}`
                        ]
                    }, e.id))
            }),
            gap.followUpKind !== 'none' && /*#__PURE__*/ _jsxs("div", {
                className: "text-xs",
                children: [
                    "Follow-up (",
                    label(gap.followUpKind),
                    "): ",
                    gap.followUpLabel
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.captureSolution.gapAssumption,
                className: "text-xs",
                children: [
                    "Underlying assumption: ",
                    gap.assumptionValidation,
                    gap.status === 'resolved' && ` — gap resolved: ${gap.resolutionNote}`
                ]
            }),
            gap.status === 'open' && /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureSolution.gapResolve,
                disabled: !note.trim(),
                onClick: ()=>onResolve(gap),
                children: "Resolve gap"
            })
        ]
    });
}
export function SolutionAlternatives({ captureProjectId }) {
    const queryClient = useQueryClient();
    const [alt, setAlt] = useState({
        name: '',
        narrative: '',
        advantages: '',
        concerns: '',
        assumptions: ''
    });
    const [rationale, setRationale] = useState('');
    const [gap, setGap] = useState({
        kind: 'requirement',
        description: '',
        followUpKind: 'none',
        followUpLabel: ''
    });
    const [evidence, setEvidence] = useState([]);
    const [ev, setEv] = useState(blankEvidence);
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const data = useQuery({
        queryKey: [
            'capture-solution-alternatives',
            captureProjectId
        ],
        queryFn: ()=>projectDataClient.getCaptureSolutionAlternatives({
                captureProjectId
            }),
        retry: false
    });
    async function run(fn) {
        setError('');
        try {
            await fn();
            await queryClient.invalidateQueries({
                queryKey: [
                    'capture-solution-alternatives',
                    captureProjectId
                ]
            });
        } catch (e) {
            setError(ConnectError.from(e).rawMessage);
        }
    }
    if (data.isLoading) return /*#__PURE__*/ _jsx("p", {
        children: "Loading alternatives…"
    });
    if (data.isError) return /*#__PURE__*/ _jsx("p", {
        role: "alert",
        children: ConnectError.from(data.error).rawMessage
    });
    const alternatives = data.data?.alternatives ?? [];
    const gaps = data.data?.gaps ?? [];
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3 rounded-md border p-3",
        children: [
            /*#__PURE__*/ _jsx("h3", {
                className: "font-semibold",
                children: "Alternatives and decision"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm",
                children: "Compare lightweight options and choose one deliberately. Choosing records a decision and its rationale; it does not change the accepted approach or any earlier revision, and rejected options stay visible."
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Decision rationale",
                hint: "Needed to select or reject.",
                testId: Testids.captureSolution.altRationale,
                value: rationale,
                onChange: setRationale
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-2",
                children: alternatives.map((a)=>/*#__PURE__*/ _jsx(AlternativeRow, {
                        alt: a,
                        rationale: rationale,
                        onDecide: (target, decision)=>run(async ()=>{
                                await projectDataClient.decideCaptureSolutionAlternative({
                                    captureProjectId,
                                    requestId: crypto.randomUUID(),
                                    alternativeId: target.id,
                                    decision,
                                    rationale
                                });
                                setRationale('');
                            })
                    }, a.id))
            }),
            [
                [
                    'name',
                    'Alternative name',
                    Testids.captureSolution.altName,
                    false
                ],
                [
                    'narrative',
                    'Narrative',
                    Testids.captureSolution.altNarrative,
                    true
                ],
                [
                    'advantages',
                    'Advantages',
                    Testids.captureSolution.altAdvantages,
                    true
                ],
                [
                    'concerns',
                    'Concerns',
                    Testids.captureSolution.altConcerns,
                    true
                ],
                [
                    'assumptions',
                    'Assumptions',
                    Testids.captureSolution.altAssumptions,
                    true
                ]
            ].map(([key, text, testId, multiline])=>/*#__PURE__*/ _jsx(TextField, {
                    label: text,
                    multiline: multiline,
                    testId: testId,
                    value: alt[key],
                    onChange: (v)=>setAlt((a)=>({
                                ...a,
                                [key]: v
                            }))
                }, key)),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureSolution.altAdd,
                disabled: !alt.name.trim(),
                onClick: ()=>run(async ()=>{
                        await projectDataClient.addCaptureSolutionAlternative({
                            captureProjectId,
                            requestId: crypto.randomUUID(),
                            alternative: alt
                        });
                        setAlt({
                            name: '',
                            narrative: '',
                            advantages: '',
                            concerns: '',
                            assumptions: ''
                        });
                    }),
                children: "Add alternative"
            }),
            /*#__PURE__*/ _jsx("h3", {
                className: "font-semibold",
                children: "Gaps"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm",
                children: "Unmet requirements, capabilities or staffing. Every gap links the evidence it rests on. Resolving a gap does not validate the underlying assumption."
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Resolution note",
                hint: "Needed to resolve a gap.",
                testId: Testids.captureSolution.gapResolveNote,
                value: note,
                onChange: setNote
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-2",
                children: gaps.map((g)=>/*#__PURE__*/ _jsx(GapRow, {
                        gap: g,
                        note: note,
                        onResolve: (target)=>run(async ()=>{
                                await projectDataClient.resolveCaptureSolutionGap({
                                    captureProjectId,
                                    gapId: target.id,
                                    resolutionNote: note
                                });
                                setNote('');
                            })
                    }, g.id))
            }),
            /*#__PURE__*/ _jsx(Choice, {
                label: "Gap kind",
                testId: Testids.captureSolution.gapKind,
                value: gap.kind,
                options: options(GAP_KINDS),
                onChange: (kind)=>setGap((g)=>({
                            ...g,
                            kind
                        }))
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Gap description",
                multiline: true,
                testId: Testids.captureSolution.gapDescription,
                value: gap.description,
                onChange: (description)=>setGap((g)=>({
                            ...g,
                            description
                        }))
            }),
            /*#__PURE__*/ _jsx(Choice, {
                label: "Follow-up",
                testId: Testids.captureSolution.gapFollowKind,
                value: gap.followUpKind,
                options: options(FOLLOW_UPS),
                onChange: (followUpKind)=>setGap((g)=>({
                            ...g,
                            followUpKind
                        }))
            }),
            gap.followUpKind !== 'none' && /*#__PURE__*/ _jsx(TextField, {
                label: "Follow-up (name the Teaming, data call, task, question or new follow-up)",
                testId: Testids.captureSolution.gapFollowLabel,
                value: gap.followUpLabel,
                onChange: (followUpLabel)=>setGap((g)=>({
                            ...g,
                            followUpLabel
                        }))
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1 text-xs",
                children: evidence.map((e, i)=>/*#__PURE__*/ _jsxs("li", {
                        children: [
                            "Evidence to link — ",
                            label(e.sourceKind),
                            ": ",
                            e.sourceLabel
                        ]
                    }, i))
            }),
            /*#__PURE__*/ _jsx(Choice, {
                label: "Evidence source kind",
                testId: Testids.captureSolution.gapEvKind,
                value: ev.sourceKind,
                options: options(SOURCE_KINDS),
                onChange: (sourceKind)=>setEv((e)=>({
                            ...e,
                            sourceKind
                        }))
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Evidence source label",
                testId: Testids.captureSolution.gapEvLabel,
                value: ev.sourceLabel,
                onChange: (sourceLabel)=>setEv((e)=>({
                            ...e,
                            sourceLabel
                        }))
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Evidence source reference",
                testId: Testids.captureSolution.gapEvRef,
                value: ev.sourceRef,
                onChange: (sourceRef)=>setEv((e)=>({
                            ...e,
                            sourceRef
                        }))
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Evidence excerpt",
                testId: Testids.captureSolution.gapEvExcerpt,
                value: ev.excerpt,
                onChange: (excerpt)=>setEv((e)=>({
                            ...e,
                            excerpt
                        }))
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureSolution.gapEvAdd,
                disabled: !ev.sourceLabel.trim(),
                onClick: ()=>{
                    setEvidence((l)=>[
                            ...l,
                            ev
                        ]);
                    setEv(blankEvidence);
                },
                children: "Add evidence"
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureSolution.gapAdd,
                disabled: !gap.description.trim() || evidence.length === 0 || gap.followUpKind !== 'none' && !gap.followUpLabel.trim(),
                onClick: ()=>run(async ()=>{
                        await projectDataClient.addCaptureSolutionGap({
                            captureProjectId,
                            requestId: crypto.randomUUID(),
                            gap: {
                                ...gap,
                                evidence
                            }
                        });
                        setGap({
                            kind: 'requirement',
                            description: '',
                            followUpKind: 'none',
                            followUpLabel: ''
                        });
                        setEvidence([]);
                    }),
                children: "Add gap"
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                "data-testid": Testids.captureSolution.altError,
                children: error
            })
        ]
    });
}
